/**
 * SyncService.js
 *
 * Singleton service that handles cloud synchronisation of user reading data.
 * Designed to slot into the existing localStorage-based state engine without
 * modifying any component, route, or Zustand store.
 *
 * Data flow:
 *   Hook mutation → scheduleSync() → debounce → _push() → POST /api/sync/push
 *   App mount / online event        → pull()   → GET  /api/sync/pull → _merge()
 *
 * Auth: Reads a Bearer JWT from localStorage key 'quran_auth_token'.
 *       This token is set by the WebAuthn login flow (implemented separately).
 *
 * Offline: If navigator.onLine is false at push time, a pending flag is written
 *           to localStorage. An 'online' listener retries the push automatically.
 */

// ─── Constants ───────────────────────────────────────────────────────────────

/** The localStorage keys that form the sync payload (matches Export schema §3). */
const SYNC_KEYS = [
  'quran_bookmarks',
  'quran_notes',
  'quran_last_read',
  'theme',
  'quran-audio-state',
  'readingMode',
  'fontSize',
  'lineSpacing',
  'fontType',
];

/** Key used to detect that a push is pending after regaining connectivity. */
const PENDING_KEY = 'quran_sync_pending';

/** Current export schema version — increment if the shape changes. */
const SCHEMA_VERSION = 1;

/** Milliseconds to wait after the last change before actually pushing. */
const DEBOUNCE_MS = 3000;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Safely parse a JSON string; returns `fallback` on error. */
function safeParse(str, fallback = null) {
  if (!str) return fallback;
  try { return JSON.parse(str); } catch { return fallback; }
}

/** Build the sync payload from the current localStorage state. */
function buildPayload() {
  const data = {};
  SYNC_KEYS.forEach((key) => {
    const raw = localStorage.getItem(key);
    if (raw !== null) {
      // Try to parse as JSON; fall back to the raw string for scalar values
      // (e.g. fontSize = "32", theme = "dark")
      try { data[key] = JSON.parse(raw); } catch { data[key] = raw; }
    }
  });
  return {
    version: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    data,
  };
}

/** Return the stored bearer token, or null if the user is not authenticated. */
function getAuthToken() {
  return localStorage.getItem('quran_auth_token') || null;
}

// ─── SyncService Singleton ────────────────────────────────────────────────────

let _debounceTimer = null;

const SyncService = {
  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * scheduleSync()
   *
   * Call this immediately after any localStorage.setItem() in a hook.
   * Uses a 3-second debounce so rapid sequential changes (e.g. font size
   * slider) are batched into a single network request.
   */
  scheduleSync() {
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(() => this._push(), DEBOUNCE_MS);
  },

  /**
   * pull()
   *
   * Fetches the server's copy of the user's data and merges it into
   * localStorage using Last-Write-Wins semantics.
   *
   * Call this once on app mount (from App.js useEffect) so the user always
   * has the latest data from whichever device last synced.
   *
   * Returns true on success, false if the user is unauthenticated or offline.
   */
  async pull() {
    const token = getAuthToken();
    if (!token) return false; // User not logged in — silent no-op

    try {
      const res = await fetch('/api/sync/pull', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        console.warn('SyncService: pull failed with status', res.status);
        return false;
      }

      const remote = await res.json();

      if (!remote?.data || typeof remote.data !== 'object') {
        console.warn('SyncService: unexpected pull response shape', remote);
        return false;
      }

      this._merge(remote.data);
      return true;
    } catch (err) {
      console.error('SyncService: pull error', err);
      return false;
    }
  },

  // ── Internal ────────────────────────────────────────────────────────────────

  /**
   * _push()
   *
   * Reads current localStorage state, builds the export-schema payload, and
   * POSTs it to /api/sync/push. If offline, queues the push for later.
   */
  async _push() {
    const token = getAuthToken();
    if (!token) return; // User not logged in — silent no-op

    if (!navigator.onLine) {
      this._queueForLater();
      return;
    }

    const payload = buildPayload();

    try {
      const res = await fetch('/api/sync/push', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        console.warn('SyncService: push failed with status', res.status);
        // Queue for retry — could be a transient server error
        this._queueForLater();
      }
    } catch (err) {
      console.error('SyncService: push error', err);
      this._queueForLater();
    }
  },

  /**
   * _merge(remoteData)
   *
   * Applies Last-Write-Wins merge logic for each syncable data type.
   * Timestamps already present in the data (updatedAt / timestamp) are used
   * as the source of truth — no artificial clocks are introduced.
   *
   * After merging, the page is reloaded so React re-hydrates from the
   * updated localStorage state. This is the same strategy used by the
   * existing Import feature in SettingsDrawer.js.
   */
  _merge(remoteData) {
    let changed = false;

    // ── 1. Last Read — scalar LWW via `updatedAt` ────────────────────────────
    const localLR = safeParse(localStorage.getItem('quran_last_read'));
    const remoteLR = remoteData['quran_last_read'];

    if (remoteLR && typeof remoteLR === 'object') {
      const localTs = localLR?.updatedAt ?? 0;
      const remoteTs = remoteLR.updatedAt ?? 0;

      if (remoteTs > localTs) {
        localStorage.setItem('quran_last_read', JSON.stringify(remoteLR));
        changed = true;
      }
    }

    // ── 2. Bookmarks — per-item LWW union via `timestamp` ───────────────────
    const localBM = safeParse(localStorage.getItem('quran_bookmarks'), []);
    const remoteBM = Array.isArray(remoteData['quran_bookmarks'])
      ? remoteData['quran_bookmarks']
      : [];

    if (remoteBM.length > 0) {
      // Build a map of local items keyed by id for O(1) lookup
      const map = new Map(localBM.map((b) => [b.id, b]));

      remoteBM.forEach((rb) => {
        const lb = map.get(rb.id);
        if (!lb) {
          // Item only exists on remote — add it
          map.set(rb.id, rb);
          changed = true;
        } else if ((rb.timestamp ?? 0) > (lb.timestamp ?? 0)) {
          // Remote is newer — overwrite
          map.set(rb.id, rb);
          changed = true;
        }
        // else: local is newer or equal — keep local, no change
      });

      if (changed) {
        localStorage.setItem(
          'quran_bookmarks',
          JSON.stringify(Array.from(map.values()))
        );
      }
    }

    // ── 3. Notes — per-item LWW union via `timestamp` ───────────────────────
    const localNotes = safeParse(localStorage.getItem('quran_notes'), []);
    const remoteNotes = Array.isArray(remoteData['quran_notes'])
      ? remoteData['quran_notes']
      : [];

    if (remoteNotes.length > 0) {
      const noteMap = new Map(localNotes.map((n) => [n.id, n]));
      let noteChanged = false;

      remoteNotes.forEach((rn) => {
        const ln = noteMap.get(rn.id);
        if (!ln) {
          noteMap.set(rn.id, rn);
          noteChanged = true;
        } else if ((rn.timestamp ?? 0) > (ln.timestamp ?? 0)) {
          noteMap.set(rn.id, rn);
          noteChanged = true;
        }
      });

      if (noteChanged) {
        localStorage.setItem(
          'quran_notes',
          JSON.stringify(Array.from(noteMap.values()))
        );
        changed = true;
      }
    }

    // ── 4. Settings — scalar LWW, only overwrite individual keys if server ──
    //    has a value we don't. We don't have per-setting timestamps yet, so
    //    we apply a conservative "remote wins only if key is missing locally"
    //    policy. This avoids overwriting a user's in-session setting changes.
    const SETTING_KEYS = [
      'theme', 'readingMode', 'fontSize', 'lineSpacing', 'fontType',
    ];
    SETTING_KEYS.forEach((key) => {
      const localVal = localStorage.getItem(key);
      const remoteVal = remoteData[key];
      if (localVal === null && remoteVal !== undefined && remoteVal !== null) {
        localStorage.setItem(
          key,
          typeof remoteVal === 'string' ? remoteVal : JSON.stringify(remoteVal)
        );
        changed = true;
      }
    });

    // ── Reload to re-hydrate React state, only if something actually changed ─
    if (changed) {
      window.location.reload();
    }
  },

  /**
   * _queueForLater()
   *
   * Writes a pending flag to localStorage. The 'online' event listener
   * at the bottom of this file will retry the push when connectivity returns.
   */
  _queueForLater() {
    localStorage.setItem(PENDING_KEY, '1');
  },
};

// ─── Offline → Online Recovery ───────────────────────────────────────────────

window.addEventListener('online', () => {
  if (localStorage.getItem(PENDING_KEY) === '1') {
    localStorage.removeItem(PENDING_KEY);
    // Small delay to let the network stack fully stabilise
    setTimeout(() => SyncService._push(), 1000);
  }
});

export default SyncService;
